// src/components/admin/PropertyModal/PropertyForm.jsx
// Formulario completo de propiedad organizado por secciones en 2 columnas
// ✅ INCLUYE: Campo para property_metadata (JSONB) + campo floor_plan_url
// ✅ Las fechas de subasta (biddingStartDate, deadline) se configuran al abrir subasta abierta
// ✅ Los incrementos (minIncrement, maxBidPercentage) se configuran al abrir subasta abierta

import React from 'react';

/**
 * Formulario completo para crear/editar propiedades
 * Organizado en 2 columnas para reducir scroll
 */
const PropertyForm = ({
  formData,
  photoUrlsText,
  adminEmailsText,
  documentUrlsText,
  error,
  isEditing,
  isPriceEditable,
  saving,
  onChange,
  onPhotoUrlsChange,
  onAdminEmailsChange,
  onDocumentUrlsChange,
  onSubmit,
  onCancel
}) => {
  return (
    <form onSubmit={onSubmit} className="p-6">
      {/* Grid de 2 columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ============== COLUMNA IZQUIERDA ============== */}
        <div className="space-y-4">

          {/* ========== INFORMACIÓN BÁSICA ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Título *</label>
            <input
              type="text"
              required
              disabled={saving}
              value={formData.title}
              onChange={(e) => onChange('title', e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50"
              placeholder="Ej: Ático con vistas al mar"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Descripción *</label>
            <textarea
              required
              disabled={saving}
              value={formData.description}
              onChange={(e) => onChange('description', e.target.value)}
              rows={3}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50"
              placeholder="Descripción breve de la propiedad..."
            />
          </div>

          {/* ========== MULTIMEDIA ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">URL del plano (opcional)</label>
            <input
              type="url"
              disabled={saving}
              value={formData.floorPlanUrl}
              onChange={(e) => onChange('floorPlanUrl', e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="https://ejemplo.com/plano.jpg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">URL del vídeo (opcional)</label>
            <input
              type="url"
              disabled={saving || !isPriceEditable}
              value={formData.videoUrl}
              onChange={(e) => onChange('videoUrl', e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="https://iframe.mediadelivery.net/embed/..."
            />
            {!isPriceEditable && (
              <p className="text-xs text-amber-400 mt-1">
                ⚠️ No se puede cambiar el vídeo una vez iniciada la subasta
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              URLs de fotos (una por línea, máx. 50)
            </label>
            <textarea
              disabled={saving}
              value={photoUrlsText}
              onChange={(e) => onPhotoUrlsChange(e.target.value)}
              rows={4}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 font-mono text-sm"
              placeholder="https://ejemplo.com/foto1.jpg&#10;https://ejemplo.com/foto2.jpg"
            />
            <p className="text-xs text-slate-400 mt-1">
              Opcional. Una URL por línea (HTTPS requerido)
            </p>
          </div>

          {/* ========== TRADUCCIÓN AUTOMÁTICA ========== */}
          <div className="flex items-center justify-between p-3 bg-slate-900/30 rounded-lg border border-slate-700/50">
            <div>
              <p className="text-sm font-medium text-slate-300">Traducción automática</p>
              <p className="text-xs text-slate-500 mt-0.5">Traduce título, descripción y datos mediante DeepL</p>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() => onChange('translationsEnabled', !formData.translationsEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
                formData.translationsEnabled ? 'bg-amber-500' : 'bg-slate-600'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  formData.translationsEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* ========== CONFIGURACIÓN DE PRECIOS ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Precio inicial *
            </label>
            <input
              type="number"
              required
              min="1"
              disabled={saving || !isPriceEditable}
              value={formData.startPrice}
              onChange={(e) => onChange('startPrice', e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="150000"
            />
            {!isPriceEditable && (
              <p className="text-xs text-amber-400 mt-1">
                ⚠️ No se puede modificar una vez iniciada la subasta
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Oferta mínima absoluta *
            </label>
            <input
              type="number"
              required
              min="1"
              disabled={saving || !isPriceEditable}
              value={formData.minAbsolute}
              onChange={(e) => onChange('minAbsolute', e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="160000"
            />
            {!isPriceEditable && (
              <p className="text-xs text-amber-400 mt-1">
                ⚠️ No se puede modificar una vez iniciada la subasta
              </p>
            )}
          </div>

        </div>
        {/* FIN COLUMNA IZQUIERDA */}

        {/* ============== COLUMNA DERECHA ============== */}
        <div className="space-y-4">

          {/* ========== DOCUMENTOS PDF ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Documentos PDF (formato: Nombre|URL)
            </label>
            <textarea
              disabled={saving}
              value={documentUrlsText}
              onChange={(e) => onDocumentUrlsChange(e.target.value)}
              rows={3}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 font-mono text-sm"
              placeholder="Nota Simple|https://ejemplo.com/nota-simple.pdf&#10;Certificado Energético|https://ejemplo.com/certificado.pdf"
            />
            <p className="text-xs text-slate-400 mt-1">
              Opcional. Una línea por documento. Formato: Nombre del documento|URL del PDF
            </p>
          </div>

          {/* ========== NOTIFICACIONES ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Emails de administrador (reciben notificaciones)
            </label>
            <textarea
              disabled={saving}
              value={adminEmailsText}
              onChange={(e) => onAdminEmailsChange(e.target.value)}
              rows={3}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 font-mono text-sm"
              placeholder="admin@ejemplo.com&#10;propietario@ejemplo.com"
            />
            <p className="text-xs text-slate-400 mt-1">
              Opcional. Un email por línea. Si está vacío, no se enviarán notificaciones.
            </p>
          </div>

          {/* ========== CONFIGURACIÓN ADICIONAL ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              URL de calendario (Calendly/Cal.com)
            </label>
            <input
              type="url"
              disabled={saving}
              value={formData.calendlyUrl || ''}
              onChange={(e) => onChange('calendlyUrl', e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50"
              placeholder="https://calendly.com/tu-usuario/visita-propiedad"
            />
            <p className="text-xs text-slate-400 mt-1">
              Opcional. URL de tu calendario para que los interesados puedan agendar visitas.
            </p>
          </div>

          {/* ========== METADATA DE PROPIEDAD (JSONB) ========== */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Metadata de propiedad (JSON)
            </label>
            <textarea
              disabled={saving}
              value={formData.propertyMetadata}
              onChange={(e) => onChange('propertyMetadata', e.target.value)}
              rows={10}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 disabled:opacity-50 font-mono text-sm"
              placeholder={`{
  "bedrooms": 3,
  "bathrooms": 2,
  "builtArea": 120,
  "usableArea": 100,
  "floor": "5º",
  "orientation": "Sur",
  "elevator": true,
  "parkingSpaces": 1,
  "storageRoom": true,
  "ibiTax": 850,
  "communityFees": 120,
  "appraisal": 380000,
  "constructionYear": 1998,
  "energyRating": "D",
  "customFields": {
    "Nota": "Reformado en 2020"
  }
}`}
            />
            <p className="text-xs text-slate-400 mt-1">
              Opcional. Información adicional de la propiedad en formato JSON.
              Acepta campos personalizados flexibles.
            </p>
          </div>

        </div>
        {/* FIN COLUMNA DERECHA */}

      </div>
      {/* FIN GRID 2 COLUMNAS */}

      {/* ========== ALERTAS (fuera del grid, ancho completo) ========== */}
      {!isPriceEditable && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mt-4">
          <p className="text-xs text-amber-400">
            ⚠️ No se pueden modificar precio inicial, oferta mínima ni vídeo una vez iniciada la subasta
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mt-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* ========== BOTONES ========== */}
      <div className="flex gap-3 pt-4 mt-6 border-t border-slate-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 bg-slate-700 text-white font-medium py-3 rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Propiedad')}
        </button>
      </div>
    </form>
  );
};

export default PropertyForm;
